import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { DocumentService } from '@app/shared/services/vault/document.service';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { ToastrService } from 'ngx-toastr';
import { CategorizedTableData, TABLE_CONFIG_BY_CATEGORY, TableConfig } from '../../../common/common-ownership-table-view/linked-ownership';
import { Subscription } from 'rxjs';
import { AddDocumentComponent } from '../../add-document.component';
import { MatDialog } from '@angular/material/dialog';
import { NonAccessTemplateComponent } from '../../linked-templates/view-template-list/non-access-template/non-access-template.component';
import { CommonOwnershipTableViewComponent } from '../../../common/common-ownership-table-view/common-ownership-table-view.component';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'role-players-association-card',
  templateUrl: './role-players-association-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolePlayersAssociationCardComponent implements OnInit, OnDestroy {
  @ViewChildren(CommonOwnershipTableViewComponent) commonOwnershipTableComponents: QueryList<CommonOwnershipTableViewComponent>;
  @Input() mode;
  @Input() data;
  @Input() origin;
  @Input() formName;
  @Input() currentuserId;
  @Input() currentFormId;
  @Input() headerText = '';
  @Input() isRemoveEditSaveSharing;
  @Input() isBeneficialOwnership = false;
  @Output() rolePlayerAssociationSelectionChange = new EventEmitter<{
    record: any;
    index: number;
    isChecked: boolean;
    categoryKey: string;
    isBeneficialOwnership: boolean;
  }>();

  stateObject;
  isDataLoading = true;
  subscription = new Subscription();
  ownedItemsList: CategorizedTableData[] = [];

  constructor(
    private dialog: MatDialog,
    private vaultService: VaultStoreEffects,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef
  ) {
    this.subscription.add(
      this.vaultService.vaultObject.subscribe((item) => {
        this.stateObject = item;
      })
    );
  }

  ngOnInit(): void {
    if (this.isBeneficialOwnership) {
      this.getVaultUserFormBeneficialOwnership();
    } else {
      this.getVaultUserFormOwnedItems();
    }
  }

  getVaultUserFormOwnedItems() {
    this.subscription.add(
      this.documentService.getVaultUserFormOwnedItems(this.currentuserId ?? this.stateObject.userId, this.currentFormId).subscribe(
        (response) => {
          this.ownedItemsList = this.buildAllOwnedItems(response ?? []);
          this.isDataLoading = false;
          this.cd.detectChanges();
        },
        (error: any) => {
          this.toastr.error('Error occurred while getting owned items', error?.error?.message);
          this.isDataLoading = false;
          this.cd.detectChanges();
        }
      )
    );
  }

  getVaultUserFormBeneficialOwnership() {
    this.subscription.add(
      this.documentService.getVaultUserFormBeneficialOwnership(this.currentuserId ?? this.stateObject.userId, this.currentFormId).subscribe(
        (response) => {
          this.ownedItemsList = this.buildAllBeneficialOwnership(response ?? []);
          this.isDataLoading = false;
          this.cd.detectChanges();
        },
        (error: any) => {
          this.toastr.error('Error occurred while getting owned items', error?.error?.message);
          this.isDataLoading = false;
          this.cd.detectChanges();
        }
      )
    );
  }

  deleteVaultUserOwnedItem(selectedItems: any) {
    const selectedItemsId = selectedItems.map((item) => item.id);
    const payload = {
      vault_user_form_id: this.currentFormId,
      ids: selectedItemsId,
    };
    this.subscription.add(
      this.documentService.deleteVaultUserFormOwnedItems(payload).subscribe((response) => {
        this.getVaultUserFormOwnedItems();
      })
    );
  }

  deleteVaultUserBeneficialOwnership(selectedItems: any) {
    const selectedItemsId = selectedItems.filter((item) => item.role_player_sub_type !== 'specific_gifts').map((item) => item.id);
    const selectedItemsOwnershipId = selectedItems.filter((item) => item.role_player_sub_type === 'specific_gifts').map((item) => item.rpoid);
    const payload = {
      vault_user_form_id: this.currentFormId,
      ...(selectedItemsId.length > 0 && { ids: selectedItemsId }),
      ...(selectedItemsOwnershipId.length > 0 && { ownership_ids: selectedItemsOwnershipId }),
    };
    this.subscription.add(
      this.documentService.deleteVaultUserFormBeneficialOwnership(payload).subscribe((response) => {
        this.getVaultUserFormBeneficialOwnership();
      })
    );
  }

  buildAllOwnedItems(items: any[]): CategorizedTableData[] {
    const categorizedOwnedItems = { assets: [], liabilities: [], entities: [] as any[] };

    for (const item of items ?? []) {
      const asset = this.isTrueFlag(item?.is_asset);
      const liability = this.isTrueFlag(item?.is_liability);
      if (asset && !liability) {
        categorizedOwnedItems.assets.push(item);
      } else if (!asset && liability) {
        categorizedOwnedItems.liabilities.push(item);
      } else {
        categorizedOwnedItems.entities.push(item);
      }
    }

    const result: CategorizedTableData[] = [];
    if (categorizedOwnedItems.assets.length) {
      result.push({ key: 'assets', title: 'Assets', items: categorizedOwnedItems.assets, tableConfig: this.buildTableConfig('assets') });
    }
    if (categorizedOwnedItems.liabilities.length) {
      result.push({
        key: 'liabilities',
        title: 'Liabilities',
        items: categorizedOwnedItems.liabilities,
        tableConfig: this.buildTableConfig('liabilities'),
      });
    }
    if (categorizedOwnedItems.entities.length) {
      result.push({ key: 'entities', title: 'Entities', items: categorizedOwnedItems.entities, tableConfig: this.buildTableConfig('entities') });
    }
    return result;
  }

  buildAllBeneficialOwnership(items: any[]): CategorizedTableData[] {
    const categorizedOwnedItems = {
      specific_gifts: [],
      general_gifts: [],
      residuary_gifts: [],
    };

    for (const item of items ?? []) {
      switch (item.role_player_sub_type) {
        case 'specific_gifts':
          categorizedOwnedItems.specific_gifts.push(item);
          break;
        case 'general_gifts':
          categorizedOwnedItems.general_gifts.push(item);
          break;
        case 'residuary_gifts':
          categorizedOwnedItems.residuary_gifts.push(item);
          break;
        default:
          categorizedOwnedItems.residuary_gifts.push(item);
          break;
      }
    }

    const result: CategorizedTableData[] = [];
    if (categorizedOwnedItems.specific_gifts.length) {
      result.push({
        key: 'specific_gifts',
        title: 'Specific Gifts',
        items: categorizedOwnedItems.specific_gifts,
        tableConfig: this.buildTableConfig('specific_gifts'),
      });
    }
    if (categorizedOwnedItems.general_gifts.length) {
      result.push({
        key: 'general_gifts',
        title: 'General Gifts',
        items: categorizedOwnedItems.general_gifts,
        tableConfig: this.buildTableConfig('general_gifts'),
      });
    }
    if (categorizedOwnedItems.residuary_gifts.length) {
      result.push({
        key: 'residuary_gifts',
        title: 'Residuary Gifts',
        items: categorizedOwnedItems.residuary_gifts,
        tableConfig: this.buildTableConfig('residuary_gifts'),
      });
    }
    return result;
  }

  openTemplate(file) {
    const constructedFile = {
      ...file,
      id: file.formid,
    };

    if (file.writeAccess && file.vault_user_form_user_id !== 'null') {
      const dialogOpen = this.dialog.open(AddDocumentComponent, {
        disableClose: true,
        data: {
          file: constructedFile,
          mode: 'edit',
          isintelligent: false,
          isfileListLoad: true,
          isFromChecklist: true,
          vufid: file.vault_user_form_id,
          vault_user_form_user_id: file.vault_user_form_user_id,
          isReport: true,
        },
        panelClass: 'full-window-dialog-add-document',
        autoFocus: false,
      });
      this.subscription.add(
        dialogOpen.afterClosed().subscribe((res) => {
          if (this.isBeneficialOwnership) {
            this.getVaultUserFormBeneficialOwnership();
          } else {
            this.getVaultUserFormOwnedItems();
          }
        })
      );
    } else if (!file.writeAccess && file.vault_user_form_user_id !== 'null') {
      this.dialog.open(NonAccessTemplateComponent, {
        panelClass: 'shared-doc-view-dialog',
        data: {
          template: file,
        },
      });
    }
  }

  onCellDataClickEventEmitterHandler(event: any): void {
    switch (event.action) {
      case 'openTemplate':
        if (!event.record?.vault_user_form_user_id) {
          return;
        }
        this.openTemplate(event.record);
        break;
      default:
        console.warn(`Unknown action: ${event.action}`);
    }
  }

  buildTableConfig(category: 'assets' | 'liabilities' | 'entities' | 'specific_gifts' | 'general_gifts' | 'residuary_gifts'): TableConfig {
    return TABLE_CONFIG_BY_CATEGORY[category];
  }

  isTrueFlag(type: any): boolean {
    return type === 1 || type === '1' || type === true || type === 'true';
  }

  onRolePlayerAssociationSelectionChange(event: { record: any; index: number; isChecked: boolean; categoryKey: string }) {
    this.rolePlayerAssociationSelectionChange.emit({
      record: event.record,
      index: event.index,
      isChecked: event.isChecked,
      categoryKey: event.categoryKey,
      isBeneficialOwnership: this.isBeneficialOwnership,
    });
    this.cd.detectChanges();
  }

  clearSelections() {
    if (this.commonOwnershipTableComponents) {
      this.commonOwnershipTableComponents.forEach((component) => {
        component.clearSelections();
      });
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
